/**
 * Staff coverage report (feat-039 F3).
 *
 * Per-location, per-date view of "who was clocked in at Square vs recording on
 * Bespoke." Driven entirely by GET /api/admin/locations/{id}/coverage (ADR 0005
 * D5) — the backend returns one row per Team Member with a precomputed `state`
 * (ok | gap | unmapped) and `overlap_minutes`. React only GROUPS and COUNTS the
 * rows into the three buckets; NO coverage math is re-implemented here
 * (feedback_metrics_single_source).
 *
 * Route: /admin/locations/:locationId/coverage (StaffRoute).
 * RBAC: Manager + Corporate + Admin — an operational tool, not admin-only
 * (ADR 0005 D5). StaffRoute already gates exactly that trio.
 *
 * B6a graceful degradation: when `labor_authorized` is false the merchant has
 * not granted Square TIMECARDS_READ; the roster still renders but the clock-in
 * data is dark, with a CTA pointing back to the location's Square card.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { AlertTriangle, ArrowLeft, CheckCircle2, HelpCircle, Loader2, Mic, MicOff } from 'lucide-react';
import ScorecardNavigation from '../ScorecardNavigation';
import { useTenant } from '../../../contexts/TenantContext';
import managementService from '../../../services/managementService';
import { CoverageReport, CoverageRow, getCoverageReport } from '../../../services/posMatchingService';

function todayIso(): string {
  // Local calendar date in YYYY-MM-DD for the <input type="date"> default.
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

const CoverageReportView: React.FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  const { selectedTenant, tenantName } = useTenant();

  const [date, setDate] = useState<string>(todayIso());
  const [locationName, setLocationName] = useState<string | null>(null);
  const [report, setReport] = useState<CoverageReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Resolve the location's display name (best-effort, like LocationAdminView).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!locationId) return;
      try {
        let list: any[] | null = null;
        if (selectedTenant) {
          const r = await managementService.getLocationsByTenant(selectedTenant);
          if (r.success && r.locations) list = r.locations;
        }
        if (!list) {
          const r = await managementService.getLocations();
          if (r.success && r.locations) list = r.locations;
        }
        const found = (list ?? []).find((l: any) => String(l.id ?? l.location_id) === String(locationId));
        if (!cancelled && found) setLocationName(found.name ?? found.location_name ?? null);
      } catch {
        /* name is cosmetic — ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [locationId, selectedTenant]);

  const load = useCallback(async () => {
    if (!locationId) {
      setError('No location selected.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await getCoverageReport(locationId, date);
      setReport(r);
    } catch (e: any) {
      console.error('CoverageReportView - load failed', e);
      setError(e?.message ?? 'Failed to load coverage report');
    } finally {
      setLoading(false);
    }
  }, [locationId, date]);

  useEffect(() => {
    load();
  }, [load]);

  // Group + count only — the source of truth for each row's bucket is the
  // backend's `state`. No coverage math in React.
  const buckets = useMemo(() => {
    const rows = report?.rows ?? [];
    return {
      ok: rows.filter((r) => r.state === 'ok'),
      gap: rows.filter((r) => r.state === 'gap'),
      unmapped: rows.filter((r) => r.state === 'unmapped'),
    };
  }, [report]);

  const windowLabel = useMemo(() => {
    if (!report?.window?.start || !report?.window?.end) return null;
    try {
      return `${format(parseISO(report.window.start), 'p')} – ${format(parseISO(report.window.end), 'p')}`;
    } catch {
      return `${report.window.start} – ${report.window.end}`;
    }
  }, [report]);

  const laborDark = report?.labor_authorized === false;

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        <button
          onClick={() => navigate(locationId ? `/admin/locations/${locationId}` : '/admin/locations')}
          className="inline-flex items-center gap-1.5 text-sm text-inkMuted hover:text-ink mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to location
        </button>

        <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-display font-semibold text-ink">Staff coverage</h1>
            <p className="text-sm text-inkMuted">
              {locationName ?? `Location ${locationId}`} · <span className="font-medium text-ink">{tenantName}</span>
              {windowLabel && <> · <span>{windowLabel}</span></>}
            </p>
          </div>
          <label className="text-sm text-ink flex items-center gap-2">
            <span className="text-inkMuted">Date</span>
            <input
              data-testid="coverage-date-input"
              type="date"
              value={date}
              max={todayIso()}
              onChange={(e) => setDate(e.target.value)}
              className="border border-softBorder rounded px-3 py-2 text-sm bg-white"
            />
          </label>
        </div>

        {laborDark && (
          <div data-testid="coverage-labor-dark" className="mb-4 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium">Clock-in data needs Square re-authorization</p>
              <p className="text-xs mt-0.5 opacity-80">
                The roster below is current, but Square hasn't granted permission to read clock-in
                times yet, so recording-vs-clocked-in status can't be shown. Re-authorize Square on
                this location's admin page to enable it.
              </p>
              <button
                onClick={() => navigate(locationId ? `/admin/locations/${locationId}` : '/admin/locations')}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-amber-300 rounded bg-white hover:bg-amber-100"
              >
                Go to Square settings
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
            {error}
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-sm text-inkMuted">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading coverage…
          </div>
        )}

        {!loading && !error && report && (
          <>
            {/* Summary counts — derived purely by counting backend-classified rows. */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <SummaryTile label="Recording" count={buckets.ok.length} tone="ok" />
              <SummaryTile label="Not recording" count={buckets.gap.length} tone="gap" />
              <SummaryTile label="Unmapped" count={buckets.unmapped.length} tone="unmapped" />
            </div>

            {report.rows.length === 0 ? (
              <div data-testid="coverage-empty" className="rounded border border-softBorder bg-white p-4 text-sm">
                <p className="text-ink font-medium">No one was clocked in at Square for this date.</p>
                <p className="text-xs text-inkMuted mt-1">
                  Pick another date, or check that Square Labor data is syncing for this location.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                <CoverageSection
                  testid="coverage-section-gap"
                  title="Clocked in, not recording"
                  subtitle="Mapped servers who were on the clock at Square but had no Bespoke recording — the coverage gap."
                  tone="gap"
                  rows={buckets.gap}
                  laborDark={laborDark}
                  emptyText="No gaps — every mapped, clocked-in server was recording."
                />
                <CoverageSection
                  testid="coverage-section-unmapped"
                  title="Clocked in, not mapped to a server"
                  subtitle="Square Team Members with no Bespoke server mapping. Map them on the location's Square card to track their coverage."
                  tone="unmapped"
                  rows={buckets.unmapped}
                  laborDark={laborDark}
                  emptyText="Every clocked-in Team Member is mapped to a server."
                />
                <CoverageSection
                  testid="coverage-section-ok"
                  title="Clocked in and recording"
                  subtitle="Mapped servers who were on the clock and recording on Bespoke."
                  tone="ok"
                  rows={buckets.ok}
                  laborDark={laborDark}
                  emptyText="No one was both clocked in and recording for this date."
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

type Tone = 'ok' | 'gap' | 'unmapped';

const TONE_STYLES: Record<Tone, { card: string; chip: string; Icon: typeof CheckCircle2 }> = {
  ok: { card: 'border-emerald-200 bg-emerald-50', chip: 'text-emerald-900', Icon: Mic },
  gap: { card: 'border-rose-200 bg-rose-50', chip: 'text-rose-900', Icon: MicOff },
  unmapped: { card: 'border-slate-200 bg-slate-50', chip: 'text-slate-800', Icon: HelpCircle },
};

const SummaryTile: React.FC<{ label: string; count: number; tone: Tone }> = ({ label, count, tone }) => {
  const s = TONE_STYLES[tone];
  const { Icon } = s;
  return (
    <div data-testid={`coverage-summary-${tone}`} className={`border rounded-lg p-3 ${s.card}`}>
      <div className={`flex items-center gap-1.5 text-xs font-medium ${s.chip}`}>
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <p className={`text-2xl font-display font-semibold mt-1 ${s.chip}`}>{count}</p>
    </div>
  );
};

const CoverageSection: React.FC<{
  testid: string;
  title: string;
  subtitle: string;
  tone: Tone;
  rows: CoverageRow[];
  laborDark: boolean;
  emptyText: string;
}> = ({ testid, title, subtitle, tone, rows, laborDark, emptyText }) => {
  const s = TONE_STYLES[tone];
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <s.Icon className={`w-4 h-4 ${s.chip}`} />
        <h2 className="text-base font-display font-semibold text-ink">{title}</h2>
        <span className="text-xs text-inkMuted">({rows.length})</span>
      </div>
      <p className="text-xs text-inkMuted mb-2">{subtitle}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-inkMuted italic px-1">{emptyText}</p>
      ) : (
        <ul className={`divide-y border rounded ${s.card}`}>
          {rows.map((r) => (
            <li
              key={r.vendor_employee_id}
              data-testid="coverage-row"
              className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm"
            >
              <div className="min-w-0">
                <p className="font-medium text-ink truncate">{r.display_name}</p>
                {r.mapped && r.internal_server_id ? (
                  <p className="text-xs text-inkMuted truncate">Server mapped</p>
                ) : (
                  <p className="text-xs text-inkMuted truncate">No server mapping</p>
                )}
              </div>
              <div className="text-right shrink-0">
                {laborDark ? (
                  <span className="text-xs text-amber-700">needs re-authorization</span>
                ) : (
                  <span className="text-xs text-inkMuted">
                    {r.overlap_minutes >= 1 ? `${Math.round(r.overlap_minutes)} min clocked in` : 'clocked in'}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default CoverageReportView;
